import React, { useState, useEffect, useCallback } from 'react';
import { auth, getIdToken, checkAdminStatus, db } from './firebase';
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { collection, addDoc, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { 
  Button, TextField, Accordion, AccordionSummary, AccordionDetails, Typography,
  Box, Grid, CircularProgress, Alert, Input, LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { handleQuery } from './App';
import Papa from 'papaparse';

// API URL'yi environment'tan al
const API_URL = process.env.REACT_APP_API_URL || 'https://api-got6faywaa-uc.a.run.app';

// Development ortamında geliştirici araçları için öneriler
if (process.env.NODE_ENV === 'development') {
  // Geliştirici araçları için öneriler
}

const DomainDetails = React.memo(({ domain, gmailResults, dnsResults, onViewDns, isLoadingDns }) => {
  const [activeTab, setActiveTab] = useState('NS');

  const renderGmailResults = () => {
    if (!gmailResults) return <Typography>Gmail sonuçları bulunamadı.</Typography>;

    return (
      <div>
        {renderRecordResult('MX Kaydı', gmailResults.mx)}
        {renderRecordResult('SPF Kaydı', gmailResults.spf)}
        {renderRecordResult('DKIM Kaydı', gmailResults.dkim)}
        {renderRecordResult('DMARC Kaydı', gmailResults.dmarc)}
      </div>
    );
  };

  const renderRecordResult = (title, data) => {
    if (!data) return null;

    return (
      <Box mb={2}>
        <Typography variant="h6">{title}</Typography>
        <Typography color={data.valid ? "primary" : "error"}>
          {data.valid ? '✓' : '✗'} {data.message}
        </Typography>
        <pre>
          {data.records && data.records.map((record, index) => (
            <div key={index}>{record}</div>
          ))}
          {data.record && <div>{data.record}</div>}
        </pre>
      </Box>
    );
  };

  const renderDnsResults = () => {
    if (!dnsResults) return <Typography>DNS sonuçları bulunamadı.</Typography>;

    const recordTypes = ['NS', 'A', 'AAAA', 'CNAME', 'MX', 'TXT'];

    let txtRecords = dnsResults.TXT ? [...dnsResults.TXT] : [];

    if (dnsResults.DKIM) {
      Object.entries(dnsResults.DKIM).forEach(([selector, record]) => {
        if (record) {
          txtRecords.push(`<span class="dkim-selector">${selector}._domainkey.</span> ${record}`);
        }
      });
    }

    if (dnsResults.DMARC && dnsResults.DMARC.length > 0) {
      txtRecords.push(`<span class="dmarc-record">_dmarc.</span> ${dnsResults.DMARC[0]}`);
    }

    const updatedResults = {
      ...dnsResults,
      TXT: txtRecords
    };

    return (
      <div className="dns-results">
        <div className="dns-tabs">
          {recordTypes.map(type => (
            <Button
              key={type}
              variant={activeTab === type ? "contained" : "outlined"}
              onClick={() => setActiveTab(type)}
              style={{ marginRight: '8px', marginBottom: '8px' }}
            >
              {type}
            </Button>
          ))}
        </div>
        <div className="dns-content">
          {recordTypes.map(type => (
            <div key={type} style={{display: activeTab === type ? 'block' : 'none'}}>
              <Typography variant="h6">{type} Kayıtları</Typography>
              <pre>
                {Array.isArray(updatedResults[type]) ? (
                  updatedResults[type].map((record, index) => (
                    <div key={`${type}-${index}`} dangerouslySetInnerHTML={{ __html: 
                      typeof record === 'object' 
                        ? JSON.stringify(record, null, 2).replace(/[{}[\]]/g, '').trim()
                        : record
                    }} />
                  ))
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: 
                    typeof updatedResults[type] === 'object'
                      ? JSON.stringify(updatedResults[type], null, 2).replace(/[{}[\]]/g, '').trim()
                      : updatedResults[type] || 'Kayıt bulunamadı.'
                  }} />
                )}
              </pre>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Typography variant="h5">Gmail Ayarları</Typography>
      {renderGmailResults()}
      <Typography variant="h5" style={{marginTop: '20px'}}>DNS Kayıtları</Typography>
      {isLoadingDns ? (
        <CircularProgress />
      ) : dnsResults ? (
        renderDnsResults()
      ) : (
        <Button variant="contained" color="primary" onClick={() => onViewDns(domain)}>
          DNS'leri Görüntüle
        </Button>
      )}
    </div>
  );
});

function AdminPage() {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [gmailResults, setGmailResults] = useState({});
  const [dnsResults, setDnsResults] = useState({});
  const [loadingDns, setLoadingDns] = useState({});
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [csvUploadProgress, setCsvUploadProgress] = useState(0);
  const [queryProgress, setQueryProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isQuerying, setIsQuerying] = useState(false);
  const [failedQueries, setFailedQueries] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addDomain();
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUser(user);
        const adminStatus = await checkAdminStatus(user.uid);
        setIsAdmin(adminStatus);
        if (adminStatus) {
          fetchUserDomains(user.uid);
          // Load saved results from localStorage
          const savedGmailResults = localStorage.getItem('gmailResults');
          const savedDnsResults = localStorage.getItem('dnsResults');
          if (savedGmailResults) setGmailResults(JSON.parse(savedGmailResults));
          if (savedDnsResults) setDnsResults(JSON.parse(savedDnsResults));
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setDomains([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserDomains = async (userId) => {
    const domainsCollection = collection(db, `users/${userId}/domains`);
    const domainsSnapshot = await getDocs(domainsCollection);
    const domainsList = domainsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setDomains(domainsList);
  };

  const addDomain = async () => {
    if (newDomain && !domains.some(domain => domain.name === newDomain) && user) {
      const domainsCollection = collection(db, `users/${user.uid}/domains`);
      await addDoc(domainsCollection, { name: newDomain });
      fetchUserDomains(user.uid);
      setNewDomain('');
    }
  };

  const deleteDomain = async (domainToDelete) => {
    if (user) {
      await deleteDoc(doc(db, `users/${user.uid}/domains`, domainToDelete.id));
      fetchUserDomains(user.uid);
      setGmailResults(prevResults => {
        const newResults = {...prevResults};
        delete newResults[domainToDelete.name];
        return newResults;
      });
      setDnsResults(prevResults => {
        const newResults = {...prevResults};
        delete newResults[domainToDelete.name];
        return newResults;
      });
    }
  };

  const deleteAllDomains = async () => {
    if (user) {
      const domainsCollection = collection(db, `users/${user.uid}/domains`);
      const domainsSnapshot = await getDocs(domainsCollection);
      domainsSnapshot.docs.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });
      setDomains([]);
      setGmailResults({});
      setDnsResults({});
    }
  };

  const queryAllDomains = useCallback(async () => {
    setIsQuerying(true);
    setQueryProgress(0);
    const idToken = await getIdToken();
    
    const newGmailResults = {};
    const newDnsResults = {};

    for (let i = 0; i < domains.length; i++) {
      const domain = domains[i];
      setLoadingDns(prev => ({...prev, [domain.name]: true}));
      try {
        const gmailResponse = await handleQuery(domain.name, 'gmail', idToken);
        newGmailResults[domain.name] = gmailResponse;
        
        const dnsResponse = await handleQuery(domain.name, 'dns', idToken);
        newDnsResults[domain.name] = dnsResponse;
      } catch (error) {
        console.error(`Error querying ${domain.name}:`, error);
        newGmailResults[domain.name] = { error: 'Sorgu hatası' };
        newDnsResults[domain.name] = { error: 'Sorgu hatası' };
      } finally {
        setLoadingDns(prev => ({...prev, [domain.name]: false}));
      }
      setQueryProgress(((i + 1) / domains.length) * 100);
      
      // Her domain sorgulandıktan sonra state'i güncelle
      setGmailResults(prev => ({...prev, ...newGmailResults}));
      setDnsResults(prev => ({...prev, ...newDnsResults}));
    }
    
    // Tüm sorgular tamamlandıktan sonra son bir kez daha güncelle
    setGmailResults(newGmailResults);
    setDnsResults(newDnsResults);
    
    // Save results to localStorage
    localStorage.setItem('gmailResults', JSON.stringify(newGmailResults));
    localStorage.setItem('dnsResults', JSON.stringify(newDnsResults));
    
    setIsQuerying(false);
  }, [domains, handleQuery, getIdToken]);

  const retryFailedQueries = async () => {
    setIsRetrying(true);
    const idToken = await getIdToken();
    
    for (let i = 0; i < failedQueries.length; i++) {
      const domain = failedQueries[i];
      try {
        const response = await handleQuery(domain, 'gmail', idToken);
        setGmailResults(prevResults => ({...prevResults, [domain]: response}));
        setFailedQueries(prev => prev.filter(d => d !== domain));
      } catch (error) {
        console.error(`Error retrying query for ${domain}:`, error);
      }
      await new Promise(resolve => setTimeout(resolve, 500)); // 500ms gecikme
    }
    
    setIsRetrying(false);
  };

  const queryDns = async (domain) => {
    if (!domain || !domain.name) {
      console.error('Invalid domain object:', domain);
      return;
    }
    
    setLoadingDns(prev => ({...prev, [domain.name]: true}));
    try {
      const idToken = await getIdToken();
      const newResults = await handleQuery(domain.name, 'dns', idToken);
      setDnsResults(prevResults => ({...prevResults, [domain.name]: newResults}));
    } catch (error) {
      console.error(`Error querying DNS for ${domain.name}:`, error);
      setDnsResults(prevResults => ({...prevResults, [domain.name]: { error: 'DNS sorgu hatası' }}));
    } finally {
      setLoadingDns(prev => ({...prev, [domain.name]: false}));
    }
  };

  // Domain kontrolü için yardımcı fonksiyon
  const isValidDomain = (domain) => {
    const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;
    return domainRegex.test(domain);
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      setCsvUploadProgress(0);
      Papa.parse(file, {
        complete: async (results) => {
          const { data } = results;
          const existingDomains = new Set(domains.map(d => d.name.toLowerCase()));
          const uniqueNewDomains = new Set();
          
          data.forEach(row => {
            const potentialDomain = row[0]?.trim();
            if (potentialDomain && isValidDomain(potentialDomain) && !existingDomains.has(potentialDomain.toLowerCase()) && !uniqueNewDomains.has(potentialDomain.toLowerCase())) {
              uniqueNewDomains.add(potentialDomain.toLowerCase());
            }
          });
          
          const domainsToAdd = Array.from(uniqueNewDomains);
          const domainsCollection = collection(db, `users/${user.uid}/domains`);
          
          for (let i = 0; i < domainsToAdd.length; i++) {
            await addDoc(domainsCollection, { name: domainsToAdd[i] });
            await new Promise(resolve => setTimeout(resolve, 1)); // 1ms gecikme
            setCsvUploadProgress(((i + 1) / domainsToAdd.length) * 100);
          }
          
          fetchUserDomains(user.uid);
          setIsUploading(false);
        }
      });
    }
  };

  const exportToGoogleSheets = async () => {
    try {
      const auth = getAuth();
      
      console.log('Starting Google Sheets export');
      
      // Google Sheets API için yeniden kimlik doğrulama yap
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/spreadsheets');
      provider.setCustomParameters({
        prompt: 'consent',
        access_type: 'offline'
      });
      
      console.log('Initiating Google Sign-In');
      const result = await signInWithPopup(auth, provider);
      console.log('Google Sign-In successful');



      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential.accessToken;
      console.log('Google access token obtained');

      // Firebase ID token'ını al
      const idToken = await auth.currentUser.getIdToken(true);
      console.log('Firebase ID token obtained');

      console.log('Preparing data for export');
      const data = domains.map(domain => {
        const gmail = gmailResults[domain.name] || {};
        const dns = dnsResults[domain.name] || {};
        return {
          Domain: domain.name,
          'MX Valid': gmail.mx?.valid ? 'Yes' : 'No',
          'SPF Valid': gmail.spf?.valid ? 'Yes' : 'No',
          'DKIM Valid': gmail.dkim?.valid ? 'Yes' : 'No',
          'DMARC Valid': gmail.dmarc?.valid ? 'Yes' : 'No',
          'MX Records': dns.MX?.map(mx => `${mx.exchange} (${mx.priority})`).join(', ') || '',
          'TXT Records': dns.TXT?.join(', ') || ''
        };
      });

      console.log('Sending request to create Google Sheet');
      const response = await fetch(`${API_URL}/api/create-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-Google-Token': token
        },
        body: JSON.stringify({ data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server responded with an error:', errorData);
        throw new Error(errorData.error || 'Failed to create Google Sheet');
      }

      const sheetData = await response.json();
      console.log('Google Sheet created successfully');
      
      if (sheetData.spreadsheetUrl) {
        // Yeni sekmede açma
        const newWindow = window.open('about:blank', '_blank');
        if (newWindow) {
          newWindow.location.href = sheetData.spreadsheetUrl;
        } else {
          alert('Pop-up engelleyici nedeniyle yeni pencere açılamadı. Lütfen pop-up engelleyiciyi kapatın ve tekrar deneyin.');
        }
      } else {
        console.error('Spreadsheet URL not found in the response');
        alert('Google Sheet oluşturuldu, ancak URL alınamadı.');
      }

    } catch (error) {
      console.error('Error exporting to Google Sheets:', error);
      alert(`Google Sheets'e aktarma sırasında bir hata oluştu: ${error.message}`);
    }
  };

  if (!user) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Bu sayfayı görüntülemek için giriş yapmanız gerekmektedir. Lütfen giriş yapın.
        </Alert>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Bu sayfayı görüntülemek için admin yetkisine sahip olmanız gerekmektedir.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Admin Sayfası</Typography>
      
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} sm={4}>
          <TextField 
            fullWidth
            value={newDomain} 
            onChange={(e) => setNewDomain(e.target.value)} 
            onKeyDown={handleKeyDown}
            placeholder="Yeni domain ekle"
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button fullWidth variant="contained" color="primary" onClick={addDomain}>Ekle</Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button 
            fullWidth 
            variant="contained" 
            color="secondary" 
            onClick={queryAllDomains}
            disabled={isQuerying}
          >
            Tümünü Sorgula
          </Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button 
            fullWidth 
            variant="contained" 
            color="primary" 
            onClick={exportToGoogleSheets}
            disabled={Object.keys(gmailResults).length === 0}
          >
            Sheets'e Aktar
          </Button>
        </Grid>
      </Grid>
        <Grid item xs={12} sm={2}>
          <Button fullWidth variant="contained" color="error" onClick={deleteAllDomains}>Tümünü Sil</Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Input
            type="file"
            inputProps={{ accept: '.csv' }}
            onChange={handleCSVUpload}
            style={{ display: 'none' }}
            id="csv-upload-input"
          />
          <label htmlFor="csv-upload-input">
            <Button fullWidth variant="contained" color="primary" component="span" disabled={isUploading}>
              CSV Yükle
            </Button>
          </label>
        </Grid>
     
      
      {isUploading && (
        <Box mb={2}>
          <Typography variant="body2">CSV Yükleniyor: {csvUploadProgress.toFixed(2)}%</Typography>
          <LinearProgress variant="determinate" value={csvUploadProgress} />
        </Box>
      )}

      {isQuerying && (
        <Box mb={2}>
          <Typography variant="body2">Domainler Sorgulanıyor: {queryProgress.toFixed(2)}%</Typography>
          <LinearProgress variant="determinate" value={queryProgress} />
        </Box>
      )}
      
      {failedQueries.length > 0 && !isQuerying && (
        <Box mb={2}>
          <Alert severity="warning">
            {failedQueries.length} domain için sorgu başarısız oldu.
            <Button color="inherit" size="small" onClick={retryFailedQueries} disabled={isRetrying}>
              Yeniden Dene
            </Button>
          </Alert>
        </Box>
      )}

      {domains.map((domain) => (
        <Accordion key={domain.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{domain.name}</Typography>
            {loadingDns[domain.name] && <CircularProgress size={20} style={{marginLeft: '10px'}} />}
            <Box ml={2}>
              <Typography component="span" color={gmailResults[domain.name]?.mx?.valid ? "primary" : "error"}>
                MX: {gmailResults[domain.name]?.mx?.valid ? '✓' : '✗'}
              </Typography>
              <Typography component="span" color={gmailResults[domain.name]?.spf?.valid ? "primary" : "error"} ml={1}>
                SPF: {gmailResults[domain.name]?.spf?.valid ? '✓' : '✗'}
              </Typography>
              <Typography component="span" color={gmailResults[domain.name]?.dkim?.valid ? "primary" : "error"} ml={1}>
                DKIM: {gmailResults[domain.name]?.dkim?.valid ? '✓' : '✗'}
              </Typography>
              <Typography component="span" color={gmailResults[domain.name]?.dmarc?.valid ? "primary" : "error"} ml={1}>
                DMARC: {gmailResults[domain.name]?.dmarc?.valid ? '✓' : '✗'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <DomainDetails 
              domain={domain.name} 
              gmailResults={gmailResults[domain.name]} 
              dnsResults={dnsResults[domain.name]}
              onViewDns={() => queryDns(domain)}
              isLoadingDns={loadingDns[domain.name]}
            />
            <Box mt={2}>
              <Button variant="outlined" color="secondary" onClick={() => deleteDomain(domain)}>
                Sil
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default AdminPage;